import { LoginResponseBodyType } from "@intelligems/ig-types/functions/user-auths/login";

import { IgStorage } from "../../utils/igStorage";

export type LoginExitResponse = Exclude<
  LoginResponseBodyType,
  { type: "login" }
>;

export type LoginExitHandler = (data: LoginExitResponse) => Promise<void>;

/**
 * `POST /v2/login` only answers with a session for `type: "login"`. A token
 * that expired, needs new Shopify scopes, or carries no email gets a
 * `redirect` (Shopify re-auth or logout) instead, and `clear` drops the stored
 * tokens. Neither carries an organization or a token, so a caller that swaps
 * sessions must leave through this instead of writing the response into the
 * session caches.
 *
 * The returned promise never settles: a mutation that awaits it from
 * `onSuccess` stays pending while the browser leaves. The token and org it
 * would have replaced are untouched, so effect-driven callers that re-switch
 * whenever those still mismatch are held off by `isPending` instead of
 * firing another `/v2/login` that would restart the navigation.
 */
export const leaveForLoginExit: LoginExitHandler = (data) => {
  if (data.type === "redirect") {
    window.location.assign(data.redirect);
  } else {
    IgStorage.clearAuthTokens();
    window.location.reload();
  }
  return new Promise<never>(() => undefined);
};

/**
 * Where a `redirect` exit should bring the user back to. The backend threads
 * it through Shopify re-auth to `/login?returnUrl=`, so a deep link that
 * needed a fresh token lands on the same record and retries the switch.
 */
export const currentAppPath = (): string =>
  window.location.pathname + window.location.search;
